import axios from 'axios';
import { storeAction } from "../storeActions.constant";
import { storeGetters } from "../storeGetters.constant";
import { storeMutation } from "../storeMutations.constant";
import { getBasicHeaders } from "../../common/utils/utils";
import {
    communityDirectoryPageConstant,
    createCommunityState,
    defaultSort,
} from "../../components/CommunityDirectoryPage/communityDirectoryPage.constants";
import { wrapAction } from '../../common/utils/actionLifecycle';
import { defaultDirectorySearchObject } from "../store.constant";
import { getAxiosCancelTokenSource } from "../../app.utils";
import uniqBy from 'lodash/uniqBy';

const communityDirectoryState = () => {
    return {
        baseURL: process.env.VUE_APP_BASE_URL,
        nextCommunityDirectorySearchOffset: 0,
        communityDirectoryResults: null,
        communityState: null,
        communitySelectedFilters: null,
        communityDirectorySort: null,
        communitySearchText: null,
        searchResultOffset: 0,
        disableCommunityDirectoryInfiniteScroll: false,
        selectedLetterFilter: null,
    };
};

const communityDirectoryPageMutations = {
    [storeMutation.setCommunityDirectorySearchText]: (state, data) => {
        state.communitySearchText = data;
    },
    [storeMutation.setCommunityDirectoryResults]: (state, data) => {
        state.communityDirectoryResults = {
            ...data,
            searchResults: data.searchResults || [],
            searchRibbonGroup: data.searchRibbonGroup || [],
        };
        state.searchResultOffset = communityDirectoryPageConstant.constant.searchResultOffsetStep;
    },
    [storeMutation.setCommunityStateFromRouter]: (state, dataFromRoute) => {
        const { communitySearchText, filters } = dataFromRoute;
        state.communitySelectedFilters = createCommunityState();
        if (filters) {
            state.communitySelectedFilters = { ...state.communitySelectedFilters, ...filters };
        }
        state.communitySearchText = communitySearchText || '';
    },
    [storeMutation.setCommunitySelectedFiltersInState]: (state, filterData) => {
        const { key, filters } = filterData;
        if (state.communitySelectedFilters && key && filters) {
            state.communitySelectedFilters[key] = [...filters];
        }
    },
    [storeMutation.removeCommunityPillFromState]: (state, pill) => {
        if (state.communitySelectedFilters) {
            Object.keys(state.communitySelectedFilters).forEach((ribbonItem) => {
                if (state.communitySelectedFilters.hasOwnProperty(ribbonItem)) {
                    const itemFound = state.communitySelectedFilters[ribbonItem].find(item => item.filterKey === pill.filterKey && item.filterValue === pill.filterValue);
                    if (itemFound) {
                        state.communitySelectedFilters[ribbonItem].splice(state.communitySelectedFilters[ribbonItem].indexOf(itemFound), 1);
                    }
                }
            });
            state.communityState = { ...state.communitySelectedFilters };
        }
    },
    [storeMutation.setCommunityDirectorySort]: (state, value) => {
        state.communityDirectorySort = value;
        if (window.sessionStorage) {
            sessionStorage.communityDirectorySort = JSON.stringify(value);
        }
    },
    [storeMutation.appendToCommunityDirectorySearchResults]: (state, data) => {
        if (data && data.searchResults && data.searchResults.length) {
            state.communityDirectoryResults = {
                ...data,
                searchResults: [...(state.communityDirectoryResults.searchResults || []), ...data.searchResults],
            };

            state.communityDirectoryResults.searchResults = uniqBy(state.communityDirectoryResults.searchResults, 'id');
            state.searchResultOffset += communityDirectoryPageConstant.constant.searchResultOffsetStep;
        }
    },
    [storeMutation.setDisableCommunityDirectoryInfiniteScroll]: (state, val) => {
        state.disableCommunityDirectoryInfiniteScroll = val;
    },
    [storeMutation.setCommunitySelectedLetterFilter]: (state, filter) => {
        state.selectedLetterFilter = filter || null;
    },
};

const communityDirectoryPageActions = {
    [storeAction.getCommunityAutoCompleteSearchResults]: wrapAction(
        storeAction.getCommunityAutoCompleteSearchResults,
        ({ state, commit }) => {
            //handling race condition
            const requestSource = getAxiosCancelTokenSource();
            const requestObject = {
                actionName: storeAction.getCommunityAutoCompleteSearchResults,
                requestSource,
            };
            commit(storeMutation.handleCancelableRequest, requestObject, { root: true });

            return axios({
                //url: `/mocks/CommunitySearch.json`,
                url: `${state.baseURL}communities/api/communitysearchautocomplete`,
                headers: getBasicHeaders(),
                cancelToken: requestSource.token,
                data: {
                    search: state.communitySearchText,
                },
                method: 'POST',
            }).then((response) => {
                commit(storeMutation.removeCancelableRequest, storeAction.getCommunityAutoCompleteSearchResults, { root: true });
                return response;
            })
                .catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getInitialCommunityDirectorySearchResults]: wrapAction(
        storeAction.getInitialCommunityDirectorySearchResults,
        ({ state, commit, dispatch }) => {
            commit(storeMutation.setDisableCommunityDirectoryInfiniteScroll, true);

            return dispatch(storeAction.getCommunityDirectoryResults, 0)
                .then((response) => {
                    commit(storeMutation.setCommunityDirectoryResults, response.data);

                    //handling infinite scroll
                    if (response && response.data && response.data.searchResults
                        && response.data.searchResults.length
                        && response.data.totalResultCount
                        && state.communityDirectoryResults.searchResults
                        && response.data.totalResultCount > state.communityDirectoryResults.searchResults.length) {
                        commit(storeMutation.setDisableCommunityDirectoryInfiniteScroll, false);
                    }

                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setDisableCommunityDirectoryInfiniteScroll, false);
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getNextCommunityDirectorySearchResults]: wrapAction(
        storeAction.getNextCommunityDirectorySearchResults,
        ({ state, commit, dispatch }) => {
            commit(storeMutation.setDisableCommunityDirectoryInfiniteScroll, true);

            return dispatch(storeAction.getCommunityDirectoryResults, state.searchResultOffset)
                .then((response) => {
                    commit(storeMutation.appendToCommunityDirectorySearchResults, response.data);

                    //handling infinite scroll
                    if (response && response.data && response.data.searchResults
                        && response.data.searchResults.length
                        && response.data.totalResultCount
                        && state.communityDirectoryResults.searchResults
                        && response.data.totalResultCount > state.communityDirectoryResults.searchResults.length) {
                        commit(storeMutation.setDisableCommunityDirectoryInfiniteScroll, false);
                    }

                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setDisableCommunityDirectoryInfiniteScroll, false);
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getCommunityDirectoryResults]: wrapAction(
        storeAction.getCommunityDirectoryResults,
        ({ state, getters }, skip) => {
            const sortObject = getters[storeGetters.communityDirectorySort];
            const sortString = `${sortObject.sortField} ${sortObject.direction}`;
            const requestBody = defaultDirectorySearchObject(
                state.communitySearchText, sortString,
                state.communitySelectedFilters,
                skip,
                communityDirectoryPageConstant.constant.searchResultMaxCount,
            );
            return axios({
                //url: '/mocks/communityDirectory.json',
                url: `${state.baseURL}communities/api/communitysearch`,
                headers: getBasicHeaders(),
                data: requestBody,
                method: 'POST',
                //method: 'GET',
            });
        },
    ),
    [storeAction.clearAllCommunityFilters]: ({ state, dispatch }) => {
        state.communitySelectedFilters = createCommunityState();
        state.communityState = { ...state.communitySelectedFilters };
        dispatch(storeAction.getCommunityDirectoryResults);
    },
};

const communityDirectoryPageGetters = {
    [storeGetters.communityDirectorySearchText]: state => state.communitySearchText,
    [storeGetters.communityDirectoryResults]: state => state.communityDirectoryResults,
    [storeGetters.communityState]: state => state.communityState,
    [storeGetters.communitySelectedFilters]: state => state.communitySelectedFilters,
    [storeGetters.communityDirectorySort]: () => defaultSort,
    [storeGetters.disableCommunityDirectoryInfiniteScroll]: state => state.disableCommunityDirectoryInfiniteScroll,
    [storeGetters.nextCommunityDirectorySearchOffset]: state => state.searchResultOffset,
    [storeGetters.communityDirectorySelectedLetterFilter]: state => state.selectedLetterFilter,
};

export default {
    namespaced: true,
    state: {
        initialState: communityDirectoryState(),
        ...communityDirectoryState(),
    },
    mutations: { ...communityDirectoryPageMutations },
    actions: { ...communityDirectoryPageActions },
    getters: { ...communityDirectoryPageGetters },
};
